import * as registeredUserActions from "../constants/registeredUserActions";

const STORAGE_KEY = "registeredUserState";

// Load state from sessionStorage
const loadState = () => {
  try {
    const serializedState = sessionStorage.getItem(STORAGE_KEY);
    if (serializedState === null) return undefined;
    const persisted = JSON.parse(serializedState);

    // Merge initial state with persisted state to ensure all keys are present
    return { ...initialState, ...persisted };
  } catch (e) {
    return undefined;
  }
};

// Save state to sessionStorage
const saveState = (state) => {
  try {
    // Specify the parts of the state to be persisted
    const persistedState = {
      userData: state.userData,
      // Add other keys you want to persist here
    };

    // Persist the rest of the state
    const serializedState = JSON.stringify(persistedState);
    sessionStorage.setItem(STORAGE_KEY, serializedState);
  } catch (e) {
    // Ignore write errors
  }
};

// Set up the initial state
const initialState = {
  loginLoading: false,
  loginSuccess: false,
  loginError: "",
  newClaimSuccess: false,
  offerLoading: false,
  offerError: "",
  getClaimedLoading: false,
  getClaimedError: "",
  getClaimedLegacyLoading: false,
  getClaimedLegacyError: "",
  userRegisterLoading: false,
  userRegisterReturnMessage: "",
  userRegisterError: "",
  currentOffersLoading: false,
  currentOffersError: "",
  userDashboardLoading: false,
  userDashboardError: "",
  resetPasswordExecuteLoading: false,
  resetPasswordExecuteSuccess: false,
  resetPasswordExecuteError: "",
  userData: {
    firstName: null,
    lastName: null,
    rewardsId: null,
    currentOffers: null,
    pendingOffers: null,
    failedOffers: null,
    claimedGiftCards: null,
    claimedLegacyGiftCards: null,
    message: "",
  },
}

const persistedState = loadState() || initialState;

export const registeredUserReducer = (state = persistedState, action) => {
  let newState;
  switch (action.type) {
    case registeredUserActions.REGISTERED_LOGIN_REQUEST:
      newState = { ...state, loginLoading: true };
      break;
    case registeredUserActions.REGISTERED_LOGIN_SUCCESS:
      newState = {
        ...state,
        loginLoading: false,
        loginSuccess: true,
        loginError: "",
        userData: {
          ...state.userData,
          firstName: action.payload.firstName,
          lastName: action.payload.lastName,
          rewardsId: action.payload.rewardsId,
        },
      };
      break;
    case registeredUserActions.REGISTERED_LOGIN_FAIL:
      newState = {
        ...state,
        loginLoading: false,
        loginSuccess: false,
        loginError: action.payload,
      };
      break;
    case registeredUserActions.REGISTERED_LOGOUT:
      newState = { ...initialState };
      break;
    case registeredUserActions.REGISTERED_CLAIM_REQUEST:
      newState = {
        ...state,
        offerLoading: true,
        newClaimSuccess: false,
        offerError: "",
      };
      break;
    case registeredUserActions.REGISTERED_CLAIM_SUCCESS:
      newState = {
        ...state,
        offerLoading: false,
        newClaimSuccess: true,
        userData: {
          ...state.userData,
          currentOffers: action.payload.giftcardList,
          pendingOffers: action.payload.giftcardListPending,
          failedOffers: action.payload.giftcardListFailed,
          claimedGiftCards: action.payload.claimedGiftCards,
          claimedLegacyGiftCards: action.payload.claimedLegacyGiftCards,
        },
        offerError: "",
      };
      break;
    case registeredUserActions.REGISTERED_CLAIM_FAIL:
      newState = {
        ...state,
        offerLoading: false,
        newClaimSuccess: false,
        offerError: action.payload,
      };
      break;
    case registeredUserActions.REGISTERED_CLAIM_ACKNOWLEDGED:
      newState = {
        ...state,
        newClaimSuccess: false,
      };
      break;
    case registeredUserActions.REGISTERED_GET_CLAIMED_REQUEST:
      newState = { ...state, getClaimedLoading: true, getClaimedError: "" };
      break;
    case registeredUserActions.REGISTERED_GET_CLAIMED_SUCCESS:
      newState = {
        ...state,
        getClaimedLoading: false,
        getClaimedError: "",
        userData: {
          ...state.userData,
          claimedGiftCards: action.payload,
        },
      };
      break;
    case registeredUserActions.REGISTERED_GET_CLAIMED_FAIL:
      newState = {
        ...state,
        getClaimedLoading: false,
        getClaimedError: action.payload,
      };
      break;
    case registeredUserActions.REGISTERED_GET_CLAIMED_LEGACY_REQUEST:
      newState = { ...state, getClaimedLegacyLoading: true, getClaimedLegacyError: "" };
      break;
    case registeredUserActions.REGISTERED_GET_CLAIMED_LEGACY_SUCCESS:
      newState = {
        ...state,
        getClaimedLegacyLoading: false,
        getClaimedLegacyError: "",
        userData: {
          ...state.userData,
          claimedLegacyGiftCards: action.payload,
        },
      };
      break;
    case registeredUserActions.REGISTERED_GET_CLAIMED_LEGACY_FAIL:
      newState = {
        ...state,
        getClaimedLegacyLoading: false,
        getClaimedLegacyError: action.payload,
      };
      break;
    case registeredUserActions.REGISTER_USER_REQUEST:
      newState = { ...state,
        userRegisterLoading: true,
        userRegisterReturnMessage: null,
        userRegisterError: null,
      };
      break;
    case registeredUserActions.REGISTER_USER_SUCCESS:
      newState = {
        ...state,
        userRegisterLoading: false,
        userRegisterReturnMessage: action.payload,
        userRegisterError: null,
      };
      break;
    case registeredUserActions.REGISTER_USER_FAIL:
      newState = {
        ...state,
        userRegisterLoading: false,
        userRegisterReturnMessage: null,
        userRegisterError: action.payload,
      };
      break;
    case registeredUserActions.REGISTERED_CURRENT_OFFERS_REQUEST:
      newState = { ...state,
        currentOffersLoading: true,
        currentOffersError: "",
      };
      break;
    case registeredUserActions.REGISTERED_CURRENT_OFFERS_SUCCESS:
      newState = {
        ...state,
        currentOffersLoading: false,
        userData: {
          ...state.userData,
          currentOffers: action.payload.giftcardList,
          pendingOffers: action.payload.giftcardListPending,
          failedOffers: action.payload.giftcardListFailed,
          claimedGiftCards: action.payload.claimedGiftCards,
          claimedLegacyGiftCards: action.payload.claimedLegacyGiftCards,
        },
        currentOffersError: "",
      };
      break;
    case registeredUserActions.REGISTERED_CURRENT_OFFERS_FAIL:
      newState = {
        ...state,
        currentOffersLoading: false,
        currentOffers: null,
        currentOffersError: action.payload,
      };
      break;
    case registeredUserActions.REGISTERED_GET_DASHBOARD_REQUEST:
      newState = { ...state,
        userDashboardLoading: true,
      };
      break;
    case registeredUserActions.REGISTERED_GET_DASHBOARD_SUCCESS:
      newState = {
        ...state,
        userDashboardLoading: false,
        userData: {
          ...state.userData,
          currentOffers: action.payload.giftcardList,
          pendingOffers: action.payload.giftcardListPending,
          failedOffers: action.payload.giftcardListFailed,
          claimedGiftCards: action.payload.claimedGiftCards,
          claimedLegacyGiftCards: action.payload.claimedLegacyGiftCards,
        },
        userDashboardError: "",
      };
      break;
    case registeredUserActions.REGISTERED_GET_DASHBOARD_FAIL:
      newState = {
        ...state,
        userDashboardLoading: false,
        userDashboardError: action.payload,
      };
      break;
    case registeredUserActions.RESET_PASSWORD_EXECUTE_REQUEST:
      newState = { ...state,
        resetPasswordExecuteLoading: true,
      };
      break;
    case registeredUserActions.RESET_PASSWORD_EXECUTE_SUCCESS:
      newState = {
        ...state,
        resetPasswordExecuteLoading: false,
        resetPasswordExecuteSuccess: true,
        resetPasswordExecuteError: "",
      };
      break;
    case registeredUserActions.RESET_PASSWORD_EXECUTE_FAIL:
      newState = {
        ...state,
        resetPasswordExecuteLoading: false,
        resetPasswordExecuteSuccess: false,
        resetPasswordExecuteError: action.payload,
      };
      break;
    default:
      newState = { ...state };
  }

  // Persist state after every action
  saveState(newState);
  return newState;
};
